import React, { useRef } from 'react';
import { Box, Typography, Button } from '@mui/material';
import { keyframes } from '@emotion/react';
import useAppTheme from 'hooks/useAppTheme';
import { useChatbotContext } from '../context/ChatbotContext';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';
import { useTranslation } from 'react-i18next';

interface SuggestionsCarouselProps {
  suggestions: string[];
  onSelect: (question: string) => void;
  isInitial?: boolean;
}

const cascadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px) scale(0.98); }
  to { opacity: 1; transform: translateY(0) scale(1); }
`;

const subtlePulseDark = keyframes`
  0%, 100% {
    border-color: #D1D5DB; /* light grey */
    box-shadow: 0 0 0 rgba(255, 165, 0, 0); /* transparent orange glow */
  }
  50% {
    border-color: #FFA403; /* orange */
    box-shadow: 0 0 10px rgba(255, 165, 0, 0.25); /* soft orange glow */
  }
`;

const subtlePulseLight = keyframes`
  0%, 100% {
    border-color: #FFFFFF;
    box-shadow: 0 0 0 rgba(30, 64, 175, 0);
  }
  50% {
    border-color: #1E40AF;
    box-shadow: 0 0 10px rgba(30, 64, 175, 0.25);
  }
`;

export const SuggestionsCarousel: React.FC<SuggestionsCarouselProps> = ({
  suggestions,
  onSelect,
  isInitial = false,
}) => {
  const { t } = useTranslation();
  const carouselRef = useRef<HTMLDivElement>(null);
  const { enableDarkTheme } = useAppTheme();
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const { isExpanded } = useChatbotContext();

  return (
    <Box sx={{ position: 'relative', px: isExpanded ? 7 : 6 }}>
      <Button
        onClick={() =>
          carouselRef.current &&
          (carouselRef.current.scrollLeft -=
            carouselRef.current.clientWidth / 2)
        }
        sx={{
          position: 'absolute',
          top: '50%',
          left: 10,
          transform: 'translateY(-50%)',
          backgroundColor: enableDarkTheme
            ? 'rgba(21, 38, 66, 0.8)'
            : 'rgba(255,255,255,0.8)',
          backdropFilter: 'blur(4px)',
          borderRadius: '9999px',
          width: '30px',
          height: '30px',
          minWidth: 0,
          color: enableDarkTheme ? '#FFFFFF' : '#26364C',
          zIndex: 10,
          '&:hover': {
            backgroundColor: enableDarkTheme ? '#152642' : '#E0E6EB',
          },
        }}
      >
        {t('larr')}
      </Button>
      <Button
        onClick={() =>
          carouselRef.current &&
          (carouselRef.current.scrollLeft +=
            carouselRef.current.clientWidth / 2)
        }
        sx={{
          position: 'absolute',
          top: '50%',
          right: 10,
          transform: 'translateY(-50%)',
          backgroundColor: enableDarkTheme
            ? 'rgba(21, 38, 66, 0.8)'
            : 'rgba(255,255,255,0.8)',
          backdropFilter: 'blur(4px)',
          borderRadius: '9999px',
          width: '30px',
          height: '30px',
          minWidth: 0,
          color: enableDarkTheme ? '#FFFFFF' : '#26364C',
          zIndex: 10,
          '&:hover': {
            backgroundColor: enableDarkTheme ? '#152642' : '#E0E6EB',
          },
        }}
      >
        {t('x2192')}
      </Button>

      <Box
        ref={carouselRef}
        sx={{
          display: 'grid',
          gridAutoFlow: 'column',
          gridTemplateRows: isExpanded ? 'repeat(2, auto)' : '1fr',
          gap: 2,
          py: 1,
          overflowX: 'auto',
          scrollBehavior: 'smooth',
          '&::-webkit-scrollbar': { display: 'none' },
          width: '100%',
        }}
      >
        {suggestions?.map((suggestion, index) => (
          <Box
            key={index}
            onClick={() => {
              onSelect(suggestion);
            }}
            sx={{
              py: 1,
              px: 1.5,
              minWidth: 'max-content',
              borderRadius: '12px',
              backgroundColor: enableDarkTheme
                ? 'rgba(100, 149, 237, 0.4)'
                : '#E3EAF6',
              border: enableDarkTheme
                ? '1px solid rgba(79, 85, 182, 0.6)'
                : '1px solid #AAB8C8',
              textAlign: 'center',
              fontWeight: 500,
              cursor: 'pointer',
              opacity: isInitial ? 0 : 1,
              animation: isInitial
                ? `${cascadeIn} 0.5s ease-out forwards, ${
                    enableDarkTheme ? subtlePulseDark : subtlePulseLight
                  } 3s infinite ease-in-out`
                : 'none',
              animationDelay: isInitial
                ? `${0.1 * (index + 1)}s, ${0.4 * (index + 1)}s`
                : undefined,
              '&:hover': {
                borderColor: enableDarkTheme ? '#FFA403' : palette.primary,
                backgroundColor: enableDarkTheme
                  ? ' rgba(100, 149, 237, 0.3)'
                  : '#D0E2F0',
                boxShadow: enableDarkTheme
                  ? '0 0 15px rgba(255, 164, 3, 0.2)'
                  : '0 0 10px rgba(54, 102, 236, 0.15)',
              },
            }}
          >
            <Typography
              sx={{
                fontSize: '12px',
                whiteSpace: 'nowrap',
                color: enableDarkTheme ? '#FFFFFF' : '#1e40af',
              }}
            >
              {suggestion}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};
